import React from 'react';
import { useTranslation } from 'react-i18next';
import { FolderStatusEnum } from '@koob/enums';
import { KoobBadge } from '@koob/ui';

const FOLDER_STATUS_COLOR = {
  [FolderStatusEnum.NEW]: 'gray',
  [FolderStatusEnum.DRAFT]: 'amber',
  [FolderStatusEnum.CONFIRMING]: 'orange',
  [FolderStatusEnum.CONFIRMED]: 'green',
  [FolderStatusEnum.ON_SPOT]: 'blue',
  [FolderStatusEnum.COMPLETED]: 'purple',
  [FolderStatusEnum.CANCELLED]: 'red',
  [FolderStatusEnum.ARCHIVED]: 'slate',
} as const;

/**
 * `previousStatus` (KD-3842): the status an archived folder had before being
 * archived, shown next to the badge as "was New".
 */
export interface FolderStatusBadgeProps {
  status?: string;
  size?: 'sm' | 'md';
  previousStatus?: string;
}

export default function FolderStatusBadge({
  status,
  size,
  previousStatus,
}: FolderStatusBadgeProps) {
  const { t } = useTranslation('folders');
  const folderStatus = status || FolderStatusEnum.NEW;
  const badge = (
    <KoobBadge dot size={size} color={FOLDER_STATUS_COLOR[folderStatus]}>
      {t(`folderStatus.${folderStatus}`)}
    </KoobBadge>
  );

  if (folderStatus !== FolderStatusEnum.ARCHIVED || !previousStatus) {
    return badge;
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      {badge}
      <span className="text-xs text-gray-500">
        {t('previousStatus', {
          status: t(`folderStatus.${previousStatus}`),
        })}
      </span>
    </span>
  );
}
